import { useWindowSize } from '@cyntler/react-doc-viewer/dist/cjs/hooks/useWindowSize'
import { Box, Pagination, Typography } from '@mui/material'
import React, { RefObject } from 'react'
import { connect } from 'react-redux'
import {
  Operations as LayoutOperations,
  operations as layoutOperations
} from 'states/dux/layout/operations'
import { Payload as LayoutPayload } from 'states/dux/layout/types'
import {
  Operations as ThemeOperations,
  operations as themeOperations
} from 'states/dux/theme/operations'
import { Payload as ThemePayload } from 'states/dux/theme/types'
import State from 'states/types'
import { mapOperationsToDispatchProps, trusty } from 'utils/common'

interface StateProps {
  theme: ThemePayload
  layout: LayoutPayload
}

interface DispatchProps {
  operations: {
    theme: ThemeOperations
    layout: LayoutOperations
  }
}

interface OwnProps {
  dataList: any[]
  childElement: React.FC<any>
  childPropSetter?: (data: any, index: number) => object
  itemsPerPage: number
  adaptiveHeight?: boolean
  containerRef?: RefObject<any>
  hidePages?: boolean
}

type AppProps = StateProps & DispatchProps & OwnProps

const NivosPaginatorFC: React.FC<any> = (props: AppProps) => {
  const {
    dataList,
    childElement,
    itemsPerPage,
    childPropSetter,
    adaptiveHeight,
    containerRef,
    hidePages
  } = props
  const childRef: RefObject<any> = React.createRef()
  const ChildElement = childElement

  const [page, setPage] = React.useState(1)
  const [count, setCount] = React.useState(
    Math.ceil(dataList.length / itemsPerPage)
  )
  const [firstItemIndex, setFII] = React.useState(0)
  const [lastItemIndex, setLII] = React.useState(itemsPerPage)
  const [minHeight, setHeight] = React.useState('fit-content')
  const [_itemsPerPage, setItemsPerPage] = React.useState(itemsPerPage)
  const size = useWindowSize()

  let propSetter = (data: any, index: number): Object => {
    return {
      data,
      key: index
    }
  }

  if (childPropSetter != null) {
    propSetter = childPropSetter
  }

  const ChildElementWithRef = React.forwardRef((p, ref) => (
    <Box ref={ref} height={'fit-content'}>
      <ChildElement {...p} />
    </Box>
  ))

  ChildElementWithRef.displayName = 'ChildElement'

  React.useEffect(() => {
    setFII((page - 1) * _itemsPerPage)
    setLII(page * _itemsPerPage)
  }, [page, _itemsPerPage])

  React.useEffect(() => {
    setPage(1)
    setFII(0)
    setLII(_itemsPerPage)
    setCount(Math.ceil(dataList.length / _itemsPerPage))
  }, [dataList, _itemsPerPage])

  // Hack to get the minHeight of the pagination container to avoid decreasing height when on last page
  React.useEffect(() => {
    if (
      dataList.length > 0 &&
      Object.keys(childRef.current).length !== 0 &&
      !trusty(adaptiveHeight)
    ) {
      if (dataList.length < _itemsPerPage) {
        const childHeight = childRef.current.getBoundingClientRect().height
        setHeight(`${_itemsPerPage * childHeight}px`)
      } else {
        setHeight('fit-content')
      }
    }
  }, [childRef, dataList, _itemsPerPage])

  React.useEffect(() => {
    if (
      trusty(adaptiveHeight) &&
      containerRef !== undefined &&
      childRef !== undefined &&
      containerRef.current !== null &&
      childRef.current !== null
    ) {
      const childHeight = childRef.current.getBoundingClientRect().height
      const parentHeight = containerRef.current.getBoundingClientRect().height
      const adaptiveItemsPerPage = Math.floor(parentHeight / childHeight) - 1
      if (adaptiveItemsPerPage > 0) {
        setItemsPerPage(adaptiveItemsPerPage)
      }
    }
  }, [size, containerRef])

  return (
    <Box
      ref={containerRef}
      width={1}
      display={'flex'}
      flexDirection={'column'}
      alignItems={'center'}
      height={trusty(adaptiveHeight) ? 1 : 'initial'}
    >
      <Box width={1} minHeight={minHeight} paddingBottom={1}>
        {dataList.length < 1 && (
          <Typography variant={'h4'} align={'center'} padding={4}>
            No Data
          </Typography>
        )}
        {dataList.length > 0 &&
          dataList
            .slice(firstItemIndex, lastItemIndex)
            .map((data, index) => (
              <ChildElementWithRef
                {...propSetter(data, index)}
                ref={childRef}
                key={index}
              />
            ))}
      </Box>
      <Pagination
        color={'primary'}
        count={count}
        page={page}
        onChange={(event, page) => setPage(page)}
        sx={{
          '.MuiPaginationItem-page': {
            display: trusty(hidePages) ? 'none' : 'initial'
          }
        }}
      />
    </Box>
  )
}

const mapStateToProps = (state: State): StateProps => {
  return {
    theme: state.theme,
    layout: state.layout
  }
}

const mapDispatchToProps = mapOperationsToDispatchProps({
  theme: themeOperations,
  layout: layoutOperations
})

const NivosPaginator = connect(
  mapStateToProps,
  mapDispatchToProps
)(NivosPaginatorFC) as React.FC<OwnProps>

export default NivosPaginator
